import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import {
  AppAlert,
  AppButton,
  AppCard,
  AppFormField,
  AppInput,
} from '../components/ui';
import UserRolesPicker from '../components/admin/UserRolesPicker';
import UserRolesManageModal from '../components/admin/UserRolesManageModal';
import UserActiveToggle from '../components/admin/UserActiveToggle';
import apiService from '../services/apiService';
import { notify } from '../components/common/AppToast';
import { ROUTES } from '../config/routes';
import useUserRoles from '../hooks/useUserRoles';
import '../styles/kkp-planning-page.scss';

const AddUserPage = () => {
  const navigate = useNavigate();
  const { roles, loading: rolesLoading, reload: reloadRoles } = useUserRoles();
  const [rolesModalOpen, setRolesModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    roles: ['ROLE_USER'],
    isActive: true
  });

  const [validationErrors, setValidationErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleRoleChange = (roleValue) => {
    setFormData(prev => {
      const currentRoles = prev.roles;
      const newRoles = currentRoles.includes(roleValue)
        ? currentRoles.filter(role => role !== roleValue)
        : [...currentRoles, roleValue];

      return {
        ...prev,
        roles: newRoles
      };
    });

    if (validationErrors.roles) {
      setValidationErrors(prev => ({
        ...prev,
        roles: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email) {
      errors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'L\'email n\'est pas valide';
    }

    if (!formData.password) {
      errors.password = 'Le mot de passe est requis';
    } else if (formData.password.length < 6) {
      errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'La confirmation du mot de passe est requise';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (!formData.firstName.trim()) {
      errors.firstName = 'Le prénom est requis';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Le nom est requis';
    }

    if (formData.roles.length === 0) {
      errors.roles = 'Au moins un rôle doit être sélectionné';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { confirmPassword, ...userData } = formData;

      const result = await apiService.makeRequest('POST', '/users', userData);

      if (result.success) {
        notify.success('Utilisateur créé avec succès — redirection…');
        setTimeout(() => {
          navigate(ROUTES.admin.users);
        }, 2000);
      } else {
        const msg = result.message || 'Erreur lors de la création de l\'utilisateur';
        setError(msg);
        notify.error(msg);
      }
    } catch (err) {
      console.error('Erreur lors de la création de l\'utilisateur:', err);
      const msg = err.message || 'Erreur lors de la création de l\'utilisateur';
      setError(msg);
      notify.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(ROUTES.admin.users);
  };

  return (
    <MasterLayout>
      <Breadcrumb
        title="Nouvel utilisateur"
        subtitle="Créer un nouveau compte utilisateur"
        actions={(
          <AppButton size="sm" variant="outline-secondary" icon="mdi:arrow-left" onClick={handleCancel}>
            Retour
          </AppButton>
        )}
      />

      {error && (
        <AppAlert>
          {error}
        </AppAlert>
      )}

      <div className="row">
        <div className="col-12">
          <AppCard className="kkp-planning-results-card" title="Informations de l'utilisateur">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <AppFormField className="col-md-6 mb-3" label="Email" htmlFor="email" required error={validationErrors.email}>
                  <AppInput
                    type="email"
                    className={validationErrors.email ? 'is-invalid' : ''}
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="exemple@email.com"
                    disabled={loading}
                  />
                </AppFormField>

                <AppFormField className="col-md-6 mb-3" label="Prénom" htmlFor="firstName" required error={validationErrors.firstName}>
                  <AppInput
                    type="text"
                    className={validationErrors.firstName ? 'is-invalid' : ''}
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Prénom"
                    disabled={loading}
                  />
                </AppFormField>

                <AppFormField className="col-md-6 mb-3" label="Nom" htmlFor="lastName" required error={validationErrors.lastName}>
                  <AppInput
                    type="text"
                    className={validationErrors.lastName ? 'is-invalid' : ''}
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Nom"
                    disabled={loading}
                  />
                </AppFormField>

                <AppFormField className="col-md-6 mb-3" label="Mot de passe" htmlFor="password" required error={validationErrors.password}>
                  <AppInput
                    type="password"
                    className={validationErrors.password ? 'is-invalid' : ''}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Mot de passe"
                    disabled={loading}
                  />
                </AppFormField>

                <AppFormField className="col-md-6 mb-3" label="Confirmer le mot de passe" htmlFor="confirmPassword" required error={validationErrors.confirmPassword}>
                  <AppInput
                    type="password"
                    className={validationErrors.confirmPassword ? 'is-invalid' : ''}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirmer le mot de passe"
                    disabled={loading}
                  />
                </AppFormField>

                <AppFormField className="col-12 mb-3" label="Rôles" required error={validationErrors.roles}>
                  <UserRolesPicker
                    roles={roles}
                    selected={formData.roles}
                    onToggle={handleRoleChange}
                    disabled={loading || rolesLoading}
                    onCreate={() => setRolesModalOpen(true)}
                  />
                </AppFormField>

                <AppFormField className="col-12 mb-3" label="Statut du compte">
                  <UserActiveToggle
                    active={formData.isActive}
                    onChange={(isActive) => setFormData((prev) => ({ ...prev, isActive }))}
                    disabled={loading}
                  />
                </AppFormField>
              </div>

              <div className="d-flex gap-2 justify-content-end">
                <AppButton variant="outline-secondary" onClick={handleCancel} disabled={loading}>
                  Annuler
                </AppButton>
                <AppButton
                  type="submit"
                  icon="mdi:content-save-outline"
                  loading={loading}
                  loadingLabel="Création..."
                >
                  Créer l'utilisateur
                </AppButton>
              </div>
            </form>
          </AppCard>
        </div>
      </div>
      <UserRolesManageModal
        show={rolesModalOpen}
        roles={roles}
        loading={rolesLoading}
        onClose={() => setRolesModalOpen(false)}
        onChanged={async (created) => {
          await reloadRoles();
          if (created?.value) {
            setFormData((prev) => (
              prev.roles.includes(created.value)
                ? prev
                : { ...prev, roles: [...prev.roles, created.value] }
            ));
            setRolesModalOpen(false);
          }
        }}
      />
    </MasterLayout>
  );
};

export default AddUserPage;
